import { useCallback, useEffect, useState } from "react";
import { apiService } from "../../../../services/api";
import { ApiError } from "../../../../services/api/client";
import { useAutoSave } from "../../../../hooks/useAutoSave";
import { useToast } from "../../../../contexts/ToastContext";
import { Area } from "../../../../types";

/**
 * Flächendaten, wie die Flächenseite sie nutzt: `Area` plus den Feldern, die nur
 * hier gebraucht werden. Ersetzt die frühere `any`-Typisierung.
 */
export interface AreaData extends Area {
  /** Status der Flächenakquise (Werte aus FlaechenakquiseStatus, backend/core/enums.py) */
  flaechenakquise_status?: string;
}

interface UseAreaDataProps {
  projectId: string;
  canEdit: boolean;
  isEditMode: boolean;
}

interface UseAreaDataReturn {
  areaData: AreaData | null;
  isLoadingArea: boolean;
  /** Ladefehler (kein 404) – von der Seite als blockierender Fehler angezeigt */
  loadError: string | null;
  flaechenbeschreibung: string;
  setFlaechenbeschreibung: (wert: string) => void;
  loadAreaData: () => Promise<void>;
}

export const useAreaData = ({
  projectId,
  canEdit,
  isEditMode,
}: UseAreaDataProps): UseAreaDataReturn => {
  const { showSuccess, showError } = useToast();

  const [areaData, setAreaData] = useState<AreaData | null>(null);
  const [isLoadingArea, setIsLoadingArea] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [flaechenbeschreibung, setFlaechenbeschreibung] = useState("");

  // Auto-Save Hook für Area-Felder
  useAutoSave(
    {
      flaechenbeschreibung,
    },
    {
      delay: 1000,
      enabled: canEdit && isEditMode,
      onSave: async (data) => {
        await apiService.updateProjectArea(projectId, data);
        showSuccess("Gespeichert");
      },
      onError: () => {
        showError("Speichern fehlgeschlagen");
      },
    },
  );

  // Lade Area-Daten
  const loadAreaData = useCallback(async () => {
    if (!projectId) return;

    try {
      setIsLoadingArea(true);
      const area: AreaData = await apiService.getProjectArea(projectId);
      setAreaData(area);
      setFlaechenbeschreibung(
        typeof area.flaechenbeschreibung === "string"
          ? area.flaechenbeschreibung
          : "",
      );
      setLoadError(null);
    } catch (error) {
      // 404 heißt: dem Projekt ist noch keine Fläche hinterlegt – kein Fehlerfall.
      const istOhneFlaeche =
        error instanceof ApiError
          ? error.statusCode === 404
          : error instanceof Error && error.message.includes("404");

      if (istOhneFlaeche) {
        setAreaData(null);
        setFlaechenbeschreibung("");
        setLoadError(null);
      } else {
        setLoadError(
          error instanceof Error
            ? error.message
            : "Flächendaten konnten nicht geladen werden",
        );
      }
    } finally {
      setIsLoadingArea(false);
    }
  }, [projectId]);

  // Lade Area-Daten beim Mount
  useEffect(() => {
    if (projectId) {
      void loadAreaData();
    }
  }, [projectId, loadAreaData]);

  return {
    areaData,
    isLoadingArea,
    loadError,
    flaechenbeschreibung,
    setFlaechenbeschreibung,
    loadAreaData,
  };
};
